import { useEffect } from "react"
import { Outlet, useMatches, useLocation } from "react-router"
import type { Locale } from "../content/types"
import Header from "../components/Header/Header"
import Footer from "../components/Footer/Footer"
import ScrollToTop from "../components/ScrollToTop"
import JsonLd from "../components/seo/JsonLd"
import { buildOrganizationSchema, buildWebSiteSchema } from "../seo/schema/organization"
import { preloadImage } from "../utils/preload"

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

function getLocaleFromMatches(matches: ReturnType<typeof useMatches>): Locale | null {
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i].handle as { locale?: Locale } | undefined
    if (handle && typeof handle === "object" && handle.locale) return handle.locale
  }
  return null
}

export default function RootLayout() {
  const matches = useMatches()
  const location = useLocation()
  const locale = getLocaleFromMatches(matches)

  useEffect(() => {
    // Manually track page view on route change (gtag itself is loaded deferred).
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
      })
    }
  }, [location])

  useEffect(() => {
    // Preload "above the fold" assets used on other routes so route switches feel instant.
    preloadImage("/card/mountains.webp").catch(() => {})
    preloadImage("/brain_assets/spark1.png").catch(() => {})
  }, [])

  useEffect(() => {
    // Swap the print-only font stylesheet to media="all" once it has loaded.
    // Runs AFTER hydration on purpose: mutating the stylesheet's media attribute
    // before hydration would cause a React hydration mismatch.
    const link = document.getElementById("font-stylesheet") as HTMLLinkElement | null
    if (!link) return
    if (link.sheet) {
      link.media = "all"
      return
    }
    const onLoad = () => {
      link.media = "all"
    }
    link.addEventListener("load", onLoad)
    return () => link.removeEventListener("load", onLoad)
  }, [])

  return (
    <div className="min-h-screen bg-site-bg font-sans text-text-primary flex flex-col">
      <JsonLd data={[buildOrganizationSchema(), buildWebSiteSchema()]} />
      <ScrollToTop />
      <Header locale={locale} />
      <main className="pt-[var(--size-layout-top-offset)] flex-grow">
        <Outlet />
      </main>
      <Footer locale={locale} />
    </div>
  )
}
